import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2 } from "lucide-react";

const POLL_MS = 8000;

export const ChatPanel = ({ coupleId, scope = "general", scopeLabel = "Conversation", compact = false }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(
    async (scrollToEnd = false) => {
      if (!coupleId) return;
      try {
        const { data } = await api.get(`/chat/${coupleId}`, { params: { scope } });
        setMessages(data || []);
        if (scrollToEnd) {
          requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
        }
      } catch (e) {
        /* silencieux pendant le polling */
      } finally {
        setLoaded(true);
      }
    },
    [coupleId, scope]
  );

  useEffect(() => {
    setLoaded(false);
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/${coupleId}`, { scope, content });
      setMessages((m) => [...m, data]);
      setText("");
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
    } catch (e) {
      toast.error("Message non envoyé. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="bg-white border border-[#E6E2DD] rounded-2xl overflow-hidden" data-testid={`chat-panel-${scope}`}>
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#F3EFEB] bg-[#FAF9F6]">
        <MessageCircle className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
        <h3 className="font-heading text-lg leading-none">{scopeLabel}</h3>
        <span className="ml-auto text-xs text-[#8A8480]">Pasteur & couple</span>
      </div>

      <div
        ref={listRef}
        className="px-5 py-4 space-y-3 overflow-y-auto"
        style={{ maxHeight: compact ? 260 : 380, minHeight: 140 }}
      >
        {!loaded ? (
          <p className="text-sm text-[#8A8480] text-center py-8">Chargement…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-[#8A8480] text-center py-8 italic">
            Aucun message pour l'instant. Démarrez la conversation.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            const isPastor = m.sender_role === "pastor";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-message">
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${
                  mine
                    ? "bg-[#C86B5E] text-white rounded-br-sm"
                    : isPastor
                      ? "bg-[#F3EFEB] text-[#2C2A29] rounded-bl-sm border border-[#E6DCD3]"
                      : "bg-[#FAF9F6] text-[#2C2A29] rounded-bl-sm border border-[#E6E2DD]"
                }`}>
                  {!mine && (
                    <p className={`text-xs font-medium mb-0.5 ${isPastor ? "text-[#C86B5E]" : "text-[#7A8B76]"}`}>
                      {m.sender_name}{isPastor ? " · Pasteur" : ""}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.content}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#8A8480]"}`}>
                    {new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="flex items-end gap-2 px-4 py-3 border-t border-[#F3EFEB] bg-[#FAF9F6]">
        <textarea
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Écrivez un message…"
          className="flex-1 bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-2.5 text-sm text-[#2C2A29] resize-none max-h-28"
        />
        <button
          data-testid="chat-send-button"
          onClick={send}
          disabled={!text.trim() || sending}
          className="flex-shrink-0 bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-40 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
          aria-label="Envoyer"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};
